import type { BusinessTypeOption } from './businessTypeGuess';

/**
 * What a website tells us about the business behind it, before the scan has
 * run. Everything here is a guess offered to the user in an editable field —
 * never a fact, and never submitted without them seeing it.
 */

/** Title segments that name a page rather than a business. */
const BOILERPLATE = /^(home|welcome|index|official site|home ?page|start)$/i;

const SEPARATORS = /\s*[|·—–‑-]\s*|\s+:\s+/;

export type NameSource = 'user' | 'title' | 'host';

export function hostOf(url: string): string {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/?#]/)[0]
    .toLowerCase();
}

/** The favicon service the prototype uses, keyed on the host alone. */
export function faviconFor(url: string): string {
  const host = hostOf(url);
  if (!host) return '';
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
}

function titleCase(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function nameFromHost(url: string): string {
  const host = hostOf(url);
  if (!host) return '';
  const label = host.split('.')[0];
  if (!label) return '';
  return titleCase(label.replace(/[-_]+/g, ' '));
}

/**
 * The brand out of a page title. Titles are usually `Brand | Tagline`, so the
 * first segment that is not boilerplate is the business. A title that is only
 * boilerplate, or missing, gives nothing.
 */
export function brandFromTitle(title?: string | null): string {
  const brand = (title ?? '')
    .split(SEPARATORS)
    .map(part => part.trim())
    .find(part => part.length > 0 && !BOILERPLATE.test(part));
  return brand && brand.length <= 60 ? brand : '';
}

function looksLikeCode(token: string): boolean {
  return /^[a-z0-9]{3,5}$/.test(token) && (token.match(/[aeiou]/g) ?? []).length <= 1;
}

function nameFromPath(url: string): string {
  const label = hostOf(url).split('.')[0];
  if (!label) return '';
  const path = url.trim().replace(/^[a-z]+:\/\//i, '').split(/[?#]/)[0].split('/').slice(1);
  const segment = path
    .map(part => {
      try { return decodeURIComponent(part); } catch { return part; }
    })
    .map(part => part.toLowerCase().replace(/\.[a-z0-9]+$/, ''))
    .reverse()
    .find(part => part.includes(label) && part.split(/[-_]+/).filter(Boolean).length > 1);
  if (!segment) return '';

  const tokens = segment.split(/[-_]+/).filter(Boolean);
  const rest = tokens.slice(1);
  const brandAt = rest.findIndex(token => token.includes(label));
  const words = looksLikeCode(tokens[0]) && brandAt > 0 ? rest : tokens;
  const name = titleCase(words.join(' '));
  return name.length <= 60 ? name : '';
}

export function businessNameFromSite({ title, url }: { title?: string | null; url: string }): string {
  return brandFromTitle(title) || nameFromPath(url) || nameFromHost(url);
}

/**
 * What a site says about itself, and the words a company-type label would use
 * for it. A concept matches an option when any of its keywords appears
 * *inside* the label, so "adventure" finds "Adventure / active travel" — the
 * firm's labels are multi-word and slashed, and we do not control them.
 *
 * Order is significance: the first concept a site matches wins, so a site that
 * says both "adventure" and "travel" is an adventure company, not an agency.
 */
const CONCEPTS: Array<{ says: RegExp; labelled: string[] }> = [
  { says: /\badventur\w*|\btrek\w*|\bclimb\w*|\bhik\w*|\brafting\b|\bkayak\w*|\bexpedition\w*|\boutdoor\b/i,
    labelled: ['adventure', 'active', 'outdoor', 'expedition', 'tour operator', 'tour'] },
  { says: /\bcruis\w*|\bsailing\b|\byacht\w*|\bvoyages?\b/i,
    labelled: ['cruise', 'sailing', 'yacht'] },
  { says: /\bairlines?\b|\bflights?\b|\bairfare\b|\baviation\b|\bcharter flight\b/i,
    labelled: ['airline', 'flight', 'air'] },
  { says: /\bcar (rental|hire)\b|\brent a car\b|\bvehicle hire\b|\bchauffeur\b|\btransfers?\b/i,
    labelled: ['car rental', 'car hire', 'transport', 'car'] },
  { says: /\bspa\b|\bwellness\b|\bretreats?\b|\bthermal\b/i,
    labelled: ['spa', 'wellness', 'retreat', 'hotel'] },
  { says: /\bvilla\w*|\bvacation rentals?\b|\bholiday lets?\b|\bself[- ]catering\b|\bapartments?\b/i,
    labelled: ['vacation rental', 'holiday rental', 'villa', 'apartment', 'hotel'] },
  { says: /\bresorts?\b/i, labelled: ['resort', 'hotel'] },
  { says: /\bhotels?\b|\binns?\b|\bb ?& ?b\b|\bbed and breakfast\b|\bguest ?house\b|\blodges?\b|\bhostels?\b/i,
    labelled: ['hotel', 'accommodation', 'lodging', 'resort'] },
  { says: /\brestaurants?\b|\bdining\b|\bbistro\b|\bbrasserie\b|\bcaf[eé]\b/i,
    labelled: ['restaurant', 'dining', 'food'] },
  { says: /\bmuseums?\b|\bgalleries\b|\battractions?\b|\btheme park\b|\bzoos?\b/i,
    labelled: ['attraction', 'experience', 'venue', 'tour operator', 'tour'] },
  { says: /\btourism board\b|\bdmo\b|\bvisit \w+\b|\bdestination marketing\b/i,
    labelled: ['tourism board', 'dmo', 'destination'] },
  { says: /\btour operators?\b|\bguided tours?\b|\bexcursions?\b|\bday trips?\b|\btours?\b/i,
    labelled: ['tour operator', 'tour'] },
  { says: /\btravel agen\w*|\btravel advisors?\b|\btravel agents?\b|\btravel\b|\bholidays?\b|\btrips?\b|\bitinerar\w*/i,
    labelled: ['travel advisor', 'travel agent', 'advisor', 'agency', 'travel'] },
];

/** A catch-all is something a person picks, never something we guess for them. */
const NEVER_GUESSED = /^(other|none|n\/a|unknown)$/i;

function labelHas(option: BusinessTypeOption, keyword: string): boolean {
  return option.label.toLowerCase().includes(keyword);
}

/**
 * The firm's own company type that best fits what a site says about itself.
 * A type the firm does not have is never invented: when the best concept has
 * no matching label the next concept is tried, and if none match the user
 * picks for themselves.
 */
export function guessCompanyType(
  text: string,
  options: BusinessTypeOption[],
): BusinessTypeOption | undefined {
  const haystack = (text || '').toLowerCase();
  if (!haystack.trim()) return undefined;

  const usable = options.filter(option => option.label && !NEVER_GUESSED.test(option.label.trim()));
  if (usable.length === 0) return undefined;

  // A site that names its own type outright is believed before anything is
  // inferred from it: "independent travel advisors" is not a cruise line just
  // because the page also mentions cruises. Longest label first, so
  // "Car Rental" is not beaten by a shorter label it contains.
  const named = usable
    .filter(option => haystack.includes(option.label.toLowerCase()))
    .sort((a, b) => b.label.length - a.label.length)[0];
  if (named) return named;

  // Then prose, where word boundaries keep the match honest. Then the same
  // concepts without boundaries, because a domain runs its words together and
  // "adventure" only appears inside "iconicadventures".
  for (const boundaries of [true, false]) {
    for (const concept of CONCEPTS) {
      const pattern = boundaries ? concept.says : looseOf(concept.says);
      if (!pattern.test(haystack)) continue;
      for (const keyword of concept.labelled) {
        const match = usable.find(option => labelHas(option, keyword));
        if (match) return match;
      }
    }
  }

  return undefined;
}

const looseCache = new Map<string, RegExp>();

/** The same concept with its word boundaries dropped, for run-together hosts. */
function looseOf(pattern: RegExp): RegExp {
  const cached = looseCache.get(pattern.source);
  if (cached) return cached;
  const loose = new RegExp(pattern.source.replace(/\\b/g, ''), 'i');
  looseCache.set(pattern.source, loose);
  return loose;
}
